import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  DollarSign,
  Users,
  TrendingUp,
  Settings,
  Eye,
  RefreshCw,
  Activity,
  GitBranch,
  Zap,
  CreditCard,
  Shield,
  AlertTriangle
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

const AdminMLMDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    matrix_stats: {
      phase1_positions: 0,
      phase2_positions: 0,
      total_positions: 0
    },
    order_stats: {
      total_orders: 0,
      total_payments: 0
    },
    pif_stats: {
      total_vouchers: 0
    },
    revenue_stats: {
      total_revenue: 0
    }
  });
  
  const [systemConfig, setSystemConfig] = useState({
    phase1_config: null,
    phase2_config: null,
    pif_config: null,
    nowpayments_config: null
  });

  useEffect(() => {
    loadMLMStats();
    loadSystemConfig();
  }, []);

  const loadMLMStats = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/matrix/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading MLM stats:', error);
      toast.error('Failed to load MLM statistics');
    }
  };

  const loadSystemConfig = async () => {
    try {
      const configs = ['phase1_config', 'phase2_config', 'pif_config', 'nowpayments_config'];
      const configData = {};
      
      for (const configKey of configs) {
        try {
          const response = await fetch(`${API_ENDPOINTS.ADMIN}/system/config/${configKey}`);
          if (response.ok) {
            const data = await response.json();
            configData[configKey] = data.config.value_json;
          }
        } catch (error) {
          console.warn(`Config ${configKey} not found`);
          configData[configKey] = null;
        }
      }
      
      setSystemConfig(configData);
    } catch (error) {
      console.error('Error loading system config:', error);
    }
  };

  const updateSystemConfig = async (key, value) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/system/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key, value })
      });

      if (response.ok) {
        toast.success(`${key} updated successfully`);
        loadSystemConfig();
      } else {
        toast.error(`Failed to update ${key}`);
      }
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigUpdate = (configKey, field, newValue) => {
    const currentConfig = systemConfig[configKey] || {};
    const updatedConfig = { ...currentConfig, [field]: newValue };
    setSystemConfig(prev => ({
      ...prev,
      [configKey]: updatedConfig
    }));
  };

  const handleSaveConfig = (configKey) => {
    updateSystemConfig(configKey, systemConfig[configKey]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">MLM System Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Manage matrix operations, configurations, and monitor system performance
          </p>
        </div>
        
        <Button onClick={loadMLMStats} className="btn-primary">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh Stats
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <GitBranch className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.matrix_stats.total_positions}</p>
                <p className="text-gray-600 text-sm">Matrix Positions</p>
                <p className="text-xs text-gray-500">
                  P1: {stats.matrix_stats.phase1_positions} | P2: {stats.matrix_stats.phase2_positions}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(stats.revenue_stats.total_revenue)}
                </p>
                <p className="text-gray-600 text-sm">Total Revenue</p>
                <p className="text-xs text-gray-500">
                  {stats.order_stats.total_payments} payments
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.order_stats.total_orders}</p>
                <p className="text-gray-600 text-sm">Total Orders</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Zap className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.pif_stats.total_vouchers}</p>
                <p className="text-gray-600 text-sm">PIF Vouchers</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Tabs */}
      <Tabs defaultValue="phase1" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="phase1">Phase 1 Config</TabsTrigger>
          <TabsTrigger value="phase2">Phase 2 Config</TabsTrigger>
          <TabsTrigger value="pif">PIF Config</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>

        {/* Phase 1 Configuration */}
        <TabsContent value="phase1" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <GitBranch className="h-5 w-5 mr-2" />
                Phase 1 (1x2 Matrix) Configuration
              </CardTitle>
              <CardDescription>
                Configure the basic credit pack and 1x2 forced matrix settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {systemConfig.phase1_config ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phase1_price">Price (USD)</Label>
                    <Input
                      id="phase1_price"
                      type="number"
                      step="0.01"
                      value={systemConfig.phase1_config.price || 5.95}
                      onChange={(e) => handleConfigUpdate('phase1_config', 'price', parseFloat(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin_cycle_nth">Admin Cycle (Every Nth)</Label>
                    <Input
                      id="admin_cycle_nth"
                      type="number"
                      value={systemConfig.phase1_config.admin_cycle_nth || 5}
                      onChange={(e) => handleConfigUpdate('phase1_config', 'admin_cycle_nth', parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin_wallet">Admin TRC-20 Wallet</Label>
                    <Input
                      id="admin_wallet"
                      value={systemConfig.phase1_config.admin_wallet || ''}
                      onChange={(e) => handleConfigUpdate('phase1_config', 'admin_wallet', e.target.value)}
                      placeholder="TRC-20 USDT wallet address"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <p className="text-gray-600">Phase 1 configuration not found</p>
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  onClick={() => handleSaveConfig('phase1_config')}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Saving...' : 'Save Phase 1 Config'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Phase 2 Configuration */}
        <TabsContent value="phase2" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Phase 2 (12x3 Matrix) Configuration
              </CardTitle>
              <CardDescription>
                Configure the three levels of Phase 2 with auto-repurchase settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {systemConfig.phase2_config ? (
                <div className="space-y-6">
                  {['L1', 'L2', 'L3'].map((level) => (
                    <div key={level} className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-4">Level {level}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Price (USD)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={systemConfig.phase2_config[level]?.price || 0}
                            onChange={(e) => {
                              const newConfig = { ...systemConfig.phase2_config };
                              newConfig[level] = { 
                                ...newConfig[level], 
                                price: parseFloat(e.target.value) 
                              };
                              setSystemConfig(prev => ({
                                ...prev,
                                phase2_config: newConfig
                              }));
                            }}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Auto-Repurchase (Every Nth Order)</Label>
                          <Input
                            type="number"
                            value={systemConfig.phase2_config[level]?.auto_repurchase_nth || 12}
                            onChange={(e) => {
                              const newConfig = { ...systemConfig.phase2_config };
                              newConfig[level] = { 
                                ...newConfig[level], 
                                auto_repurchase_nth: parseInt(e.target.value) 
                              };
                              setSystemConfig(prev => ({
                                ...prev,
                                phase2_config: newConfig
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <p className="text-gray-600">Phase 2 configuration not found</p>
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  onClick={() => handleSaveConfig('phase2_config')}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Saving...' : 'Save Phase 2 Config'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PIF Configuration */}
        <TabsContent value="pif" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Pay-It-Forward (PIF) Configuration
              </CardTitle>
              <CardDescription>
                Configure PIF requirements and compliance settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {systemConfig.pif_config ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Phase 2 Unlock Requirement</Label>
                    <Input
                      type="number"
                      value={systemConfig.pif_config.phase2_unlock_requirement || 12}
                      onChange={(e) => handleConfigUpdate('pif_config', 'phase2_unlock_requirement', parseInt(e.target.value))}
                    />
                    <p className="text-xs text-gray-500">PIFs required to unlock Phase 2</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Compliance Window (Days)</Label>
                    <Input
                      type="number"
                      value={systemConfig.pif_config.compliance_window_days || 3}
                      onChange={(e) => handleConfigUpdate('pif_config', 'compliance_window_days', parseInt(e.target.value))}
                    />
                    <p className="text-xs text-gray-500">Days to complete minimum PIF</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Min PIF per Window</Label>
                    <Input
                      type="number"
                      value={systemConfig.pif_config.min_pif_per_window || 1}
                      onChange={(e) => handleConfigUpdate('pif_config', 'min_pif_per_window', parseInt(e.target.value))}
                    />
                    <p className="text-xs text-gray-500">Minimum PIFs per window</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <p className="text-gray-600">PIF configuration not found</p>
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  onClick={() => handleSaveConfig('pif_config')}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Saving...' : 'Save PIF Config'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Configuration */}
        <TabsContent value="payments" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                NowPayments Configuration
              </CardTitle>
              <CardDescription>
                Configure payment processing and webhook settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {systemConfig.nowpayments_config ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <Input
                        type="password"
                        value={systemConfig.nowpayments_config.api_key || ''}
                        onChange={(e) => handleConfigUpdate('nowpayments_config', 'api_key', e.target.value)}
                        placeholder="NowPayments API Key"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>IPN Secret</Label>
                      <Input
                        type="password"
                        value={systemConfig.nowpayments_config.ipn_secret || ''}
                        onChange={(e) => handleConfigUpdate('nowpayments_config', 'ipn_secret', e.target.value)}
                        placeholder="IPN Secret for webhooks"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Default Coin</Label>
                      <Input
                        value={systemConfig.nowpayments_config.default_coin || 'USDTTRC20'}
                        onChange={(e) => handleConfigUpdate('nowpayments_config', 'default_coin', e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Webhook URL</Label>
                      <Input
                        value={systemConfig.nowpayments_config.webhook_url || ''}
                        onChange={(e) => handleConfigUpdate('nowpayments_config', 'webhook_url', e.target.value)}
                        placeholder="https://your-domain.com/api/webhooks/nowpayments"
                      />
                    </div>
                  </div>

                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-start">
                      <Shield className="h-5 w-5 text-yellow-600 mr-2 mt-1" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-semibold mb-2">Non-Custodial Notice:</p>
                        <p>BBC Quick List never holds customer funds. All payments are peer-to-peer between members via NowPayments. We are not affiliated with NowPayments - this integration is for convenience only.</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <p className="text-gray-600">NowPayments configuration not found</p>
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  onClick={() => handleSaveConfig('nowpayments_config')}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Saving...' : 'Save Payment Config'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminMLMDashboard;